import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Gift, 
  Send, 
  Plus, 
  Trash2, 
  Eye, 
  Check, 
  X, 
  Copy,
  Euro,
  Calendar,
  User,
  Mail,
  QrCode,
  ArrowLeft
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const GiftCardsPage = () => {
  const navigate = useNavigate();
  const [giftCards, setGiftCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [qrData, setQrData] = useState(null);
  const [previewHtml, setPreviewHtml] = useState('');
  const [creating, setCreating] = useState(false);
  const [formData, setFormData] = useState({
    amount: 50,
    recipient_name: '',
    recipient_email: '',
    sender_name: '',
    sender_email: '',
    message: '',
    language: 'fr'
  });

  const token = localStorage.getItem('ebb_token');
  const amounts = [25, 50, 100, 150, 200];

  const apiCall = async (endpoint, method = 'GET', body = null) => {
    const response = await fetch(`${BACKEND_URL}${endpoint}`, {
      method,
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}`,
      },
      body: body ? JSON.stringify(body) : null,
    });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.detail || 'API Error');
    }
    return response.json();
  };

  const loadGiftCards = async () => {
    try {
      setLoading(true);
      const data = await apiCall('/api/gift-cards');
      setGiftCards(data.gift_cards || []);
    } catch (error) {
      console.error('Error loading gift cards:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadGiftCards();
  }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      await apiCall('/api/gift-cards', 'POST', formData);
      setShowCreateModal(false);
      setFormData({
        amount: 50,
        recipient_name: '',
        recipient_email: '',
        sender_name: '',
        sender_email: '',
        message: '',
        language: 'fr'
      });
      loadGiftCards();
    } catch (error) {
      alert(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDelete = async (cardId) => {
    if (!window.confirm('Supprimer cette carte cadeau ?')) return;
    try {
      await apiCall(`/api/gift-cards/${cardId}`, 'DELETE');
      loadGiftCards();
    } catch (error) {
      alert(error.message);
    }
  };

  const handlePreview = async () => {
    try {
      const data = await apiCall(`/api/gift-cards/preview-email`, 'POST', {
        recipient_name: formData.recipient_name || "Marie Martin",
        sender_name: formData.sender_name || "Jean Dupont",
        amount: formData.amount || 100,
        message: formData.message || "",
        language: formData.language || "fr"
      });
      setPreviewHtml(data.html);
      setShowPreviewModal(true);
    } catch (error) {
      alert(error.message);
    }
  };

  const handleShowQR = async (cardId, cardCode) => {
    try {
      const data = await apiCall(`/api/gift-cards/${cardId}/qrcode`);
      setQrData({ ...data, cardCode });
      setShowQRModal(true);
    } catch (error) {
      alert(error.message);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    alert('Code copié !');
  };

  const getStatusBadge = (card) => {
    if (card.status === 'used' || card.balance <= 0) {
      return <span className="px-2 py-1 text-xs rounded-full bg-gray-100 text-gray-600">Épuisée</span>;
    }
    if (card.status === 'expired') {
      return <span className="px-2 py-1 text-xs rounded-full bg-red-100 text-red-600">Expirée</span>;
    }
    return <span className="px-2 py-1 text-xs rounded-full bg-green-100 text-green-600">Active</span>;
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto space-y-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-amber-700 to-amber-600 rounded-2xl p-8 text-white">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3">
                <Gift className="w-10 h-10" />
                Cartes Cadeaux
              </h1>
              <p className="text-amber-100 mt-2">Créez et gérez les cartes cadeaux de votre boutique</p>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/admin')}
                className="flex items-center gap-2 bg-white/20 hover:bg-white/30 px-4 py-2 rounded-lg transition-colors"
                data-testid="back-to-admin-btn"
              >
                <ArrowLeft className="w-5 h-5" />
                Retour
              </button>
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center gap-2 bg-white text-amber-700 px-6 py-3 rounded-xl font-semibold hover:bg-amber-50 transition-colors shadow-lg"
              >
                <Plus className="w-5 h-5" />
                Nouvelle carte
              </button>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          {[
            { label: 'Total créées', value: giftCards.length, icon: Gift, color: 'amber' },
            { label: 'Actives', value: giftCards.filter(c => c.status === 'active' && c.balance > 0).length, icon: Check, color: 'green' },
            { label: 'Épuisées', value: giftCards.filter(c => c.balance <= 0).length, icon: X, color: 'gray' },
            { label: 'Valeur totale', value: `€${giftCards.reduce((sum, c) => sum + c.initial_amount, 0).toFixed(0)}`, icon: Euro, color: 'blue' },
          ].map((stat, i) => (
            <div key={i} className={`bg-white rounded-xl p-5 shadow-sm border-l-4 border-${stat.color}-500`}>
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{stat.label}</p>
                  <p className={`text-2xl font-bold text-${stat.color}-600`}>{stat.value}</p>
                </div>
                <stat.icon className={`w-8 h-8 text-${stat.color}-300`} />
              </div>
            </div>
          ))}
        </div>

        {/* Gift Cards List */}
        <div className="bg-white rounded-xl shadow-sm overflow-hidden">
          <div className="p-6 border-b">
            <h2 className="text-lg font-semibold">Liste des cartes cadeaux</h2>
          </div>
          
          {loading ? (
            <div className="p-12 text-center">
              <div className="w-8 h-8 border-4 border-amber-600 border-t-transparent rounded-full animate-spin mx-auto"></div>
            </div>
          ) : giftCards.length === 0 ? (
            <div className="p-12 text-center text-gray-500">
              <Gift className="w-16 h-16 mx-auto mb-4 text-gray-300" />
              <p>Aucune carte cadeau créée</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left p-4 font-medium text-gray-600">Code</th>
                    <th className="text-left p-4 font-medium text-gray-600">Destinataire</th>
                    <th className="text-left p-4 font-medium text-gray-600">Expéditeur</th>
                    <th className="text-center p-4 font-medium text-gray-600">Montant</th>
                    <th className="text-center p-4 font-medium text-gray-600">Solde</th>
                    <th className="text-center p-4 font-medium text-gray-600">Statut</th>
                    <th className="text-center p-4 font-medium text-gray-600">Expire le</th>
                    <th className="text-center p-4 font-medium text-gray-600">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {giftCards.map(card => (
                    <tr key={card.id} className="border-t hover:bg-gray-50">
                      <td className="p-4">
                        <button
                          onClick={() => copyCode(card.code)}
                          className="flex items-center gap-2 font-mono text-amber-700 hover:text-amber-800"
                        >
                          {card.code}
                          <Copy className="w-4 h-4" />
                        </button>
                      </td>
                      <td className="p-4">
                        <div className="font-medium">{card.recipient_name}</div>
                        <div className="text-sm text-gray-500">{card.recipient_email}</div>
                      </td>
                      <td className="p-4 text-gray-600">{card.sender_name}</td>
                      <td className="p-4 text-center font-semibold text-gray-900">€{card.initial_amount}</td>
                      <td className="p-4 text-center">
                        <span className={`font-semibold ${card.balance > 0 ? 'text-green-600' : 'text-gray-400'}`}>
                          €{card.balance.toFixed(2)}
                        </span>
                      </td>
                      <td className="p-4 text-center">{getStatusBadge(card)}</td>
                      <td className="p-4 text-center text-sm text-gray-500">{card.expiry_date}</td>
                      <td className="p-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => handleShowQR(card.id, card.code)}
                            className="p-2 text-amber-600 hover:bg-amber-50 rounded-lg"
                            title="Voir QR Code"
                          >
                            <QrCode className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => handleDelete(card.id)}
                            className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                            title="Supprimer"
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Create Modal */}
        {showCreateModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
              <div className="p-6 border-b bg-gradient-to-r from-amber-700 to-amber-600 text-white rounded-t-2xl">
                <h2 className="text-xl font-bold flex items-center gap-2">
                  <Gift className="w-6 h-6" />
                  Créer une carte cadeau
                </h2>
              </div>
              
              <form onSubmit={handleCreate} className="p-6 space-y-5">
                {/* Amount Selection */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-3">Montant</label>
                  <div className="flex flex-wrap gap-2">
                    {amounts.map(amount => (
                      <button
                        key={amount}
                        type="button"
                        onClick={() => setFormData(prev => ({ ...prev, amount }))}
                        className={`px-6 py-3 rounded-xl font-semibold transition-all ${
                          formData.amount === amount
                            ? 'bg-amber-600 text-white shadow-lg scale-105'
                            : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                        }`}
                      >
                        €{amount}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Recipient */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <User className="w-4 h-4 inline mr-1" />
                      Nom du destinataire *
                    </label>
                    <input
                      type="text"
                      required
                      value={formData.recipient_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, recipient_name: e.target.value }))}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                      placeholder="Marie Martin"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      <Mail className="w-4 h-4 inline mr-1" />
                      Email destinataire *
                    </label>
                    <input
                      type="email"
                      required
                      value={formData.recipient_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, recipient_email: e.target.value }))}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                      placeholder="marie@email.com"
                    />
                  </div>
                </div>

                {/* Sender */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Nom de l'expéditeur *</label>
                    <input
                      type="text"
                      required
                      value={formData.sender_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, sender_name: e.target.value }))}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                      placeholder="Jean Dupont"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Email expéditeur</label>
                    <input
                      type="email"
                      value={formData.sender_email}
                      onChange={(e) => setFormData(prev => ({ ...prev, sender_email: e.target.value }))}
                      className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                      placeholder="jean@email.com"
                    />
                  </div>
                </div>

                {/* Message */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Message personnalisé</label>
                  <textarea
                    value={formData.message}
                    onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                    rows={3}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                    placeholder="Joyeux anniversaire ! 🎉"
                  />
                </div>

                {/* Language */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Langue de l'email</label>
                  <select
                    value={formData.language}
                    onChange={(e) => setFormData(prev => ({ ...prev, language: e.target.value }))}
                    className="w-full px-4 py-2 border rounded-lg focus:ring-2 focus:ring-amber-500"
                  >
                    <option value="fr">Français</option>
                    <option value="nl">Nederlands</option>
                  </select>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4 border-t">
                  <button
                    type="button"
                    onClick={handlePreview}
                    className="flex-1 px-4 py-3 border-2 border-amber-600 text-amber-600 rounded-xl font-medium hover:bg-amber-50 flex items-center justify-center gap-2"
                  >
                    <Eye className="w-5 h-5" />
                    Aperçu
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowCreateModal(false)}
                    className="px-6 py-3 border rounded-xl hover:bg-gray-50"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    disabled={creating}
                    className="flex-1 px-4 py-3 bg-amber-600 text-white rounded-xl font-semibold hover:bg-amber-700 disabled:opacity-50 flex items-center justify-center gap-2"
                  >
                    {creating ? (
                      <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Créer & Envoyer
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {/* Preview Modal */}
        {showPreviewModal && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-3xl max-h-[90vh] overflow-hidden">
              <div className="p-4 border-b flex justify-between items-center">
                <h3 className="font-semibold">Aperçu de l'email</h3>
                <button onClick={() => setShowPreviewModal(false)} className="p-2 hover:bg-gray-100 rounded-lg">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <div className="overflow-auto max-h-[70vh]">
                <iframe
                  srcDoc={previewHtml}
                  title="Email Preview"
                  className="w-full h-[600px] border-0"
                />
              </div>
            </div>
          </div>
        )}

        {/* QR Code Modal */}
        {showQRModal && qrData && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
              <div className="p-6 bg-gradient-to-r from-amber-700 to-amber-600 text-white text-center">
                <Gift className="w-10 h-10 mx-auto mb-2" />
                <h3 className="text-xl font-bold">Carte Cadeau</h3>
                <p className="text-amber-200 font-mono mt-2">{qrData.code}</p>
              </div>
              <div className="p-6 text-center">
                <img 
                  src={qrData.qr_code} 
                  alt="QR Code" 
                  className="mx-auto w-48 h-48 rounded-lg shadow-md"
                />
                <p className="mt-4 text-2xl font-bold text-amber-700">€{qrData.balance?.toFixed(2)}</p>
                <p className="text-sm text-gray-500">Solde disponible</p>
                <div className="mt-4 p-3 bg-gray-50 rounded-lg">
                  <p className="text-xs text-gray-600">
                    Scannez ce QR code en caisse ou entrez le code manuellement lors du paiement.
                  </p>
                </div>
              </div>
              <div className="p-4 border-t flex gap-2">
                <button
                  onClick={() => copyCode(qrData.code)}
                  className="flex-1 px-4 py-2 bg-amber-100 text-amber-700 rounded-lg font-medium hover:bg-amber-200 flex items-center justify-center gap-2"
                >
                  <Copy className="w-4 h-4" />
                  Copier le code
                </button>
                <button
                  onClick={() => setShowQRModal(false)}
                  className="px-4 py-2 border rounded-lg hover:bg-gray-50"
                >
                  Fermer
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GiftCardsPage;
